import React, { useState } from 'react';
import { Coffee, Wind, Trophy, Building2, Moon, Star, Phone, Instagram, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { menuData, testimonials, experiences } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  wind: Wind,
  coffee: Coffee,
  trophy: Trophy,
  building: Building2,
  moon: Moon
};

const Home = () => {
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      toast.success(response.data.message || 'Thank you for contacting us! We\'ll get back to you soon.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email: newsletterEmail });
      toast.success(response.data.message || 'Successfully subscribed to our newsletter!');
      setNewsletterEmail('');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="fixed top-0 w-full bg-stone-900/98 backdrop-blur-md z-50 border-b border-stone-800/50 shadow-xl">
        <div className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_skyline-shisha/artifacts/jvf9unig_logo.webp" 
            alt="Nour L'Fenna" 
            className="h-11 sm:h-14 md:h-16 lg:h-[4.5rem] w-auto object-contain"
            style={{ mixBlendMode: 'multiply' }}
          />
          <nav className="hidden md:flex items-center gap-10">
            <a href="#about" className="text-stone-200 hover:text-amber-200 transition-all duration-300 font-medium">About</a>
            <a href="#experiences" className="text-stone-200 hover:text-amber-200 transition-all duration-300 font-medium">Experiences</a>
            <a href="#menu" className="text-stone-200 hover:text-amber-200 transition-all duration-300 font-medium">Menu</a>
            <a href="#contact" className="text-stone-200 hover:text-amber-200 transition-all duration-300 font-medium">Contact</a>
          </nav>
          {/* Mobile Navigation - Compact Single Row */}
          <nav className="md:hidden flex items-center gap-4 text-xs">
            <a href="#about" className="text-stone-300 hover:text-amber-200 transition-colors">About</a>
            <a href="#menu" className="text-stone-300 hover:text-amber-200 transition-colors">Menu</a>
            <a href="#contact" className="text-stone-300 hover:text-amber-200 transition-colors">Contact</a>
          </nav>
          <Button className="bg-amber-600 hover:bg-amber-700 text-white font-semibold px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base transition-all duration-300 hover:shadow-lg hover:shadow-amber-600/50 hover:scale-105">
            <a href="tel:+971563212203">Call Now</a>
          </Button>
        </div>
      </header>

      {/* Hero Section - Desktop Only */}
      <section className="relative h-screen hidden md:flex items-end justify-center overflow-hidden">
        {/* Background Image - Desktop */}
        <div
          className="absolute inset-0 bg-cover bg-center transform scale-105"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_skyline-shisha/artifacts/rqkv43at_Screenshot%202026-06-04%20at%206.36.53%E2%80%AFPM.png)',
            backgroundPosition: 'center 40%'
          }}>
        </div>
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/90"></div>
        
        {/* Hero Content - Desktop */}
        <div className="relative z-10 text-center px-6 pb-8 max-w-3xl mx-auto">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-7 tracking-tight leading-tight drop-shadow-2xl">
            Where Dubai Slows Down Over Coffee
          </h1>
          <p className="text-xl text-white/95 mb-12 max-w-2xl mx-auto leading-relaxed drop-shadow-xl font-light">
            A 24/7 rooftop lounge serving specialty coffee, premium shisha, desserts, and unforgettable late-night experiences.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <Button size="lg" className="bg-amber-600 hover:bg-amber-500 text-white px-10 py-7 text-lg font-semibold shadow-2xl hover:shadow-amber-500/60 transition-all duration-300 hover:scale-105 rounded-lg">
              <a href="#menu">View Menu</a>
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white bg-white/20 backdrop-blur-lg text-white hover:bg-white hover:text-stone-900 px-10 py-7 text-lg font-semibold shadow-2xl transition-all duration-300 hover:scale-105 rounded-lg">
              <a href="#contact">Contact Us</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Mobile Hero - Promotional Image + Open 24/7 */}
      <section className="md:hidden pt-[3.75rem] bg-gradient-to-br from-amber-700 via-amber-800 to-stone-800">
        <img 
          src="https://customer-assets.emergentagent.com/job_skyline-shisha/artifacts/5g8b3sbu_WhatsApp%20Image%202026-06-11%20at%2014.48.20.png"
          alt="Nour L'Fenna Rooftop Lounge - More Than Just A Coffee"
          className="w-full h-auto block"
        />
        <div className="py-6 px-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtNi42MjcgNS4zNzMtMTIgMTItMTJzMTIgNS4zNzMgMTIgMTItNS4zNzMgMTItMTIgMTItMTItNS4zNzMtMTItMTJ6bS0yNCAwYzAtNi42MjcgNS4zNzMtMTIgMTItMTJzMTIgNS4zNzMgMTIgMTItNS4zNzMgMTItMTIgMTItMTItNS4zNzMtMTItMTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
          <div className="text-center relative z-10">
            <Clock className="w-14 h-14 mx-auto mb-4 drop-shadow-2xl" />
            <h2 className="text-2xl font-bold mb-3 tracking-tight leading-tight">Open Around The Clock</h2>
            <p className="text-sm leading-relaxed font-light text-white/95">
              Whether it's morning coffee or late-night shisha, Nour L'Fenna remains your go-to destination in Dubai, open 24/7.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-28 bg-white">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 mb-8 tracking-tight">About Nour L'Fenna</h2>
          <p className="text-lg md:text-xl text-stone-700 leading-relaxed mb-6">
            Nour L'Fenna is more than a café, it's a destination. Located in the heart of Al Sufouh, Dubai, our 24/7 rooftop lounge welcomes you to experience authentic Arabic hospitality combined with modern comfort.
          </p>
          <p className="text-lg md:text-xl text-stone-700 leading-relaxed">
            Whether you're here for morning coffee, watching the big game with friends, enjoying premium shisha, or unwinding late at night, we offer a welcoming atmosphere with stunning skyline views. Come as you are, stay as long as you like.
          </p>
        </div>
      </section>

      {/* Signature Experiences */}
      <section id="experiences" className="py-28 bg-gradient-to-b from-stone-50 to-stone-100">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center text-stone-900 mb-20 tracking-tight">Signature Experiences</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {experiences.map((exp, index) => {
              const Icon = iconMap[exp.icon];
              return (
                <Card key={index} className="bg-white border-stone-200/50 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group rounded-2xl overflow-hidden">
                  <CardContent className="p-10">
                    <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-7 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <Icon className="w-10 h-10 text-amber-700" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-5 leading-tight">{exp.title}</h3>
                    <p className="text-stone-600 leading-relaxed text-base md:text-lg">{exp.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Menu Highlights */}
      <section id="menu" className="py-28 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center text-stone-900 mb-20 tracking-tight">Menu</h2>
          
          <div className="max-w-7xl mx-auto space-y-20">
            {/* Coffee */}
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-stone-800 mb-10 flex items-center gap-4 justify-center">
                <Coffee className="w-10 h-10 text-amber-700" />
                Coffee
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {menuData.coffee.map((item, idx) => (
                  <div key={idx} className="bg-stone-50 p-6 rounded-xl hover:bg-stone-100 transition-all duration-300 hover:shadow-lg border border-stone-200/50">
                    <h4 className="text-lg font-bold text-stone-900">{item.name}</h4>
                  </div>
                ))}
              </div>
            </div>

            {/* Iced Coffee */}
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-stone-800 mb-10 text-center">Iced Coffee</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {menuData.icedCoffee.map((item, idx) => (
                  <div key={idx} className="bg-stone-50 p-6 rounded-xl hover:bg-stone-100 transition-all duration-300 hover:shadow-lg border border-stone-200/50">
                    <h4 className="text-lg font-bold text-stone-900">{item.name}</h4>
                  </div>
                ))}
              </div>
            </div>

            {/* Tea */}
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-stone-800 mb-10 text-center">Tea</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {menuData.tea.map((item, idx) => (
                  <div key={idx} className="bg-stone-50 p-6 rounded-xl hover:bg-stone-100 transition-all duration-300 hover:shadow-lg border border-stone-200/50">
                    <h4 className="text-lg font-bold text-stone-900">{item.name}</h4>
                  </div>
                ))}
              </div>
            </div>

            {/* Fresh Juices */}
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-stone-800 mb-10 text-center">Fresh Juices</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {menuData.juices.map((item, idx) => (
                  <div key={idx} className="bg-stone-50 p-6 rounded-xl hover:bg-stone-100 transition-all duration-300 hover:shadow-lg border border-stone-200/50">
                    <h4 className="text-lg font-bold text-stone-900">{item.name}</h4>
                  </div>
                ))}
              </div>
            </div>

            {/* Mojitos */}
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-stone-800 mb-10 text-center">Mojitos</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {menuData.mojitos.map((item, idx) => (
                  <div key={idx} className="bg-stone-50 p-6 rounded-xl hover:bg-stone-100 transition-all duration-300 hover:shadow-lg border border-stone-200/50">
                    <h4 className="text-lg font-bold text-stone-900">{item.name}</h4>
                  </div>
                ))}
              </div>
            </div>

            {/* Mocktails */}
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-stone-800 mb-10 text-center">Mocktails</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {menuData.mocktails.map((item, idx) => (
                  <div key={idx} className="bg-stone-50 p-6 rounded-xl hover:bg-stone-100 transition-all duration-300 hover:shadow-lg border border-stone-200/50">
                    <h4 className="text-lg font-bold text-stone-900">{item.name}</h4>
                  </div>
                ))}
              </div>
            </div>

            {/* Milkshakes */}
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-stone-800 mb-10 text-center">Milkshakes</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {menuData.milkshakes.map((item, idx) => (
                  <div key={idx} className="bg-stone-50 p-6 rounded-xl hover:bg-stone-100 transition-all duration-300 hover:shadow-lg border border-stone-200/50">
                    <h4 className="text-lg font-bold text-stone-900">{item.name}</h4>
                  </div>
                ))}
              </div>
            </div>

            {/* Desserts */}
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-stone-800 mb-10 text-center">Desserts</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {menuData.desserts.map((item, idx) => (
                  <div key={idx} className="bg-stone-50 p-6 rounded-xl hover:bg-stone-100 transition-all duration-300 hover:shadow-lg border border-stone-200/50">
                    <h4 className="text-lg font-bold text-stone-900">{item.name}</h4>
                  </div>
                ))}
              </div>
            </div>

            {/* Premium Shisha */}
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-stone-800 mb-10 flex items-center gap-4 justify-center">
                <Wind className="w-10 h-10 text-amber-700" />
                Premium Shisha
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {menuData.shisha.map((item, idx) => (
                  <div key={idx} className="bg-stone-50 p-6 rounded-xl hover:bg-stone-100 transition-all duration-300 hover:shadow-lg border border-stone-200/50">
                    <h4 className="text-lg font-bold text-stone-900">{item.name}</h4>
                  </div>
                ))}
              </div>
            </div>

            {/* Soft Drinks */}
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-stone-800 mb-10 text-center">Soft Drinks</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {menuData.softDrinks.map((item, idx) => (
                  <div key={idx} className="bg-stone-50 p-6 rounded-xl hover:bg-stone-100 transition-all duration-300 hover:shadow-lg border border-stone-200/50">
                    <h4 className="text-lg font-bold text-stone-900">{item.name}</h4>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-28 bg-gradient-to-b from-stone-50 to-stone-100">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center text-stone-900 mb-20 tracking-tight">What Our Guests Say</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {testimonials.map((review, idx) => (
              <Card key={idx} className="bg-white border-stone-200/50 shadow-lg hover:shadow-2xl transition-all duration-500 rounded-2xl">
                <CardContent className="p-10">
                  <div className="flex gap-1 mb-6">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                  <p className="text-stone-700 mb-8 leading-relaxed text-lg">
                    "{review.text}"
                  </p>
                  <div className="flex justify-between items-center border-t border-stone-200 pt-6">
                    <p className="font-bold text-stone-900 text-lg">{review.name}</p>
                    <p className="text-sm text-stone-500 font-medium">{review.date}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Open 24/7 Section */}
      <section className="py-32 bg-gradient-to-br from-amber-700 via-amber-800 to-stone-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtNi42MjcgNS4zNzMtMTIgMTItMTJzMTIgNS4zNzMgMTIgMTItNS4zNzMgMTItMTIgMTItMTItNS4zNzMtMTItMTJ6bS0yNCAwYzAtNi42MjcgNS4zNzMtMTIgMTItMTJzMTIgNS4zNzMgMTIgMTItNS4zNzMgMTItMTIgMTItMTItNS4zNzMtMTItMTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
        <div className="container mx-auto px-6 text-center max-w-5xl relative z-10">
          <Clock className="w-20 h-20 mx-auto mb-8 drop-shadow-2xl" />
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 tracking-tight leading-tight">Open Around The Clock</h2>
          <p className="text-xl md:text-2xl leading-relaxed font-light text-white/95">
            Whether it's morning coffee or late-night shisha, Nour L'Fenna remains your go-to destination in Dubai, open 24/7.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-28 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center text-stone-900 mb-20 tracking-tight">Get In Touch</h2>
          
          <div className="grid lg:grid-cols-2 gap-16 max-w-7xl mx-auto">
            {/* Contact Form */}
            <div>
              <h3 className="text-3xl font-bold text-stone-900 mb-8">Send Us a Message</h3>
              <form onSubmit={handleContactSubmit} className="space-y-7">
                <div>
                  <Input
                    placeholder="Your Name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    required
                    className="border-stone-300 py-6 px-5 text-base rounded-xl"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="Your Email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    required
                    className="border-stone-300 py-6 px-5 text-base rounded-xl"
                  />
                </div>
                <div>
                  <Input
                    type="tel"
                    placeholder="Your Phone"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="border-stone-300 py-6 px-5 text-base rounded-xl"
                  />
                </div>
                <div>
                  <Textarea
                    placeholder="Your Message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    required
                    rows={6}
                    className="border-stone-300 p-5 text-base rounded-xl"
                  />
                </div>
                <Button type="submit" disabled={isSubmitting} className="w-full bg-amber-600 hover:bg-amber-700 text-white py-7 text-lg font-semibold rounded-xl shadow-xl hover:shadow-amber-600/50 transition-all duration-300 hover:scale-[1.02]">
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Info & Map */}
            <div className="space-y-10">
              <div>
                <h3 className="text-3xl font-bold text-stone-900 mb-8">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-5">
                    <Phone className="w-7 h-7 text-amber-700 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-stone-900 text-lg mb-2">Phone / WhatsApp</p>
                      <a href="tel:+971563212203" className="text-amber-700 hover:text-amber-800 text-lg font-medium">+971 56 321 2203</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-5">
                    <Instagram className="w-7 h-7 text-amber-700 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-stone-900 text-lg mb-2">Instagram</p>
                      <a href="https://instagram.com/nouralfenna" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:text-amber-800 text-lg font-medium">@nouralfenna</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-5">
                    <MapPin className="w-7 h-7 text-amber-700 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-stone-900 text-lg mb-2">Location</p>
                      <p className="text-stone-700 text-base">Sidra Tower - Sheikh Zayed Rd</p>
                      <p className="text-stone-700 text-base">Al Sufouh - Al Sufouh 1 - Dubai</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-5">
                    <Mail className="w-7 h-7 text-amber-700 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-stone-900 text-lg mb-2">Email</p>
                      <a href="mailto:admin@nourlfenna.com" className="text-amber-700 hover:text-amber-800 text-base font-medium block">admin@nourlfenna.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-5">
                    <Clock className="w-7 h-7 text-amber-700 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-stone-900 text-lg mb-2">Opening Hours</p>
                      <p className="text-stone-700 text-base">24 Hours - Open All Day, Every Day</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div className="h-80 rounded-2xl overflow-hidden shadow-2xl">
                <iframe
                  title="Nour L'Fenna Location - Sidra Tower"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3614.0662447891376!2d55.17456507539775!3d25.096746577701856!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f6ca7be636bfd%3A0x9c8a07d7e8c93d1!2sSidra%20Tower%20-%20Sheikh%20Zayed%20Rd%20-%20Al%20Sufouh%20-%20Dubai!5e0!3m2!1sen!2sae!4v1638450000000!5m2!1sen!2sae"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-stone-900">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">Stay Updated</h3>
          <p className="text-stone-300 mb-10 text-lg">Subscribe to our newsletter for exclusive offers and updates</p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              required
              className="bg-white border-stone-700 flex-1 py-6 px-5 text-base rounded-xl"
            />
            <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white font-semibold px-10 py-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-amber-600/50">
              <Send className="w-5 h-5" />
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-950 text-stone-400 py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_skyline-shisha/artifacts/jvf9unig_logo.webp" 
                alt="Nour L'Fenna" 
                className="h-20 w-auto mb-6"
              />
              <p className="text-stone-400 leading-relaxed">Where Dubai slows down over coffee. Your 24/7 rooftop destination.</p>
            </div>
            <div>
              <h5 className="text-white font-bold mb-6 text-lg">Quick Links</h5>
              <ul className="space-y-3">
                <li><a href="#about" className="hover:text-amber-200 transition-colors text-base">About</a></li>
                <li><a href="#experiences" className="hover:text-amber-200 transition-colors text-base">Experiences</a></li>
                <li><a href="#menu" className="hover:text-amber-200 transition-colors text-base">Menu</a></li>
                <li><a href="#contact" className="hover:text-amber-200 transition-colors text-base">Contact</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-white font-bold mb-6 text-lg">Connect</h5>
              <ul className="space-y-3">
                <li>
                  <a href="tel:+971563212203" className="hover:text-amber-200 transition-colors text-base">+971 56 321 2203</a>
                </li>
                <li>
                  <a href="https://instagram.com/nouralfenna" target="_blank" rel="noopener noreferrer" className="hover:text-amber-200 transition-colors text-base">@nouralfenna</a>
                </li>
                <li className="text-stone-400 text-base">Sidra Tower - Sheikh Zayed Rd</li>
                <li className="text-stone-400 text-base">Al Sufouh 1, Dubai</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-800 pt-8 text-center">
            <p className="text-stone-500">&copy; 2025 Nour L'Fenna Coffee Lounge. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;